/**
 * Tears down a previously-installed service worker and the shells it precached.
 *
 * The counterpart to `registerServiceWorkerDynamic`, for when the PostHog `pwa` flag
 * is OFF. Turning the flag off only stops the app from *registering* a worker — any
 * worker a user already installed keeps running, keeps answering every navigation
 * from its precache, and never learns about a deploy, because the code that checks
 * for updates is behind the same flag that was just switched off. That turns the
 * kill switch into a permanent stale-app trap for exactly the users who had used the
 * app the most. Unregistering is what actually returns them to the network.
 */

/** Caches this app creates: Workbox's precache plus our runtime icon-chunk cache. */
const isAppCache = (cacheName: string): boolean =>
  cacheName.startsWith('workbox-') || cacheName === 'icon-chunks'

export async function unregisterServiceWorkers(): Promise<void> {
  if (!('serviceWorker' in navigator)) return

  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    if (!registrations.length) return

    await Promise.all(
      registrations.map((registration) => registration.unregister()),
    )

    // Unregistering stops the worker but leaves its precache behind; without this the
    // next registration could still be served stale entries out of the old cache.
    if (!('caches' in window)) return
    const cacheNames = await caches.keys()
    await Promise.all(
      cacheNames.filter(isAppCache).map((name) => caches.delete(name)),
    )
  } catch {
    /* best-effort cleanup — never block bootstrap on it */
  }
}
